import React from "react";
import { Box, Chip, CircularProgress, Tooltip } from "@mui/material";
import { Copy, FileCheck2, FileMinus2, FileX2, Image as ImageIcon, UserCheck } from "lucide-react";
import type { IngestStatus } from "../../../chat/hooks/useStreamingTurn";

export interface IngestProgressProps {
  ingests: IngestStatus[];
}

/**
 * Inline knowledge-base ingestion progress. When a file is uploaded or a
 * tool ingests a document, the backend streams document_ingest frames
 * (processing → ready / failed / duplicate / duplicate_personal, or for an
 * image pending_media / skipped); this
 * renders one chip per document that advances in place. Failed chips expose
 * their error via tooltip, and a duplicate_personal chip explains itself the
 * same way (it's informational only — see PromoteVisibilityDialog/KnowledgePage
 * for the actual promote action, out of scope for this in-chip chip). Every
 * chip's aria-label is prefixed with its status so it reads clearly and is
 * testable independent of the icon.
 */
export function IngestProgress({ ingests }: IngestProgressProps) {
  if (!ingests.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, my: 0.5 }} aria-live="polite">
      {ingests.map((d, i) => {
        const name = d.filename ?? (d.document_id != null ? `Document ${d.document_id}` : "Document");
        const key = d.document_id != null ? `id-${d.document_id}` : `name-${d.filename ?? i}`;
        const icon =
          d.status === "processing" ? (
            <CircularProgress size={11} thickness={6} />
          ) : d.status === "ready" ? (
            <FileCheck2 size={12} />
          ) : d.status === "duplicate" ? (
            <Copy size={12} />
          ) : d.status === "duplicate_personal" ? (
            <UserCheck size={12} />
          ) : d.status === "pending_media" ? (
            <ImageIcon size={12} />
          ) : d.status === "skipped" ? (
            <FileMinus2 size={12} />
          ) : (
            <FileX2 size={12} />
          );
        const color =
          d.status === "ready"
            ? "primary.main"
            : d.status === "failed"
              ? "error.main"
              : "text.secondary";
        const ariaStatus =
          d.status === "ready"
            ? "Ready"
            : d.status === "failed"
              ? "Failed"
              : d.status === "duplicate"
                ? "Duplicate"
                : d.status === "duplicate_personal"
                  ? "Already in your library"
                  : d.status === "pending_media"
                    ? "Stored"
                    : d.status === "skipped"
                      ? "Not added"
                      : "Processing";
        // Stored or skipped images are outcomes, not errors: neutral styling.
        const neutral =
          d.status === "duplicate" ||
          d.status === "duplicate_personal" ||
          d.status === "pending_media" ||
          d.status === "skipped";
        const chip = (
          <Chip
            key={key}
            size="small"
            variant={d.status === "processing" ? "outlined" : "filled"}
            icon={icon}
            label={name}
            aria-label={`${ariaStatus}: ${name}`}
            sx={{
              ...(d.status === "processing"
                ? { color: "text.secondary", borderColor: "divider" }
                : neutral
                  ? { bgcolor: "action.selected", color: "text.primary" }
                  : { bgcolor: color, color: d.status === "ready" ? "primary.contrastText" : "error.contrastText" }),
              "& .MuiChip-icon": { color: "inherit", ml: 0.75 },
            }}
          />
        );
        if (d.status === "failed" && d.error) {
          return (
            <Tooltip key={key} title={d.error}>
              {chip}
            </Tooltip>
          );
        }
        if ((d.status === "pending_media" || d.status === "skipped") && d.message) {
          return (
            <Tooltip key={key} title={d.message}>
              {chip}
            </Tooltip>
          );
        }
        if (d.status === "duplicate_personal") {
          return (
            <Tooltip
              key={key}
              title="Already in your Personal library — open Knowledge to change its visibility"
            >
              {chip}
            </Tooltip>
          );
        }
        return chip;
      })}
    </Box>
  );
}
